import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Phone, PhoneCall } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { LogPhoneCallDialog } from "./LogPhoneCallDialog";

interface ClickToCallButtonProps {
  phoneNumber: string | null | undefined;
  customerName: string;
  orderId?: string;
  customerId?: string;
  variant?: "icon" | "button";
  size?: "sm" | "default";
}

export function ClickToCallButton({
  phoneNumber,
  customerName,
  orderId,
  customerId,
  variant = "icon",
  size = "sm",
}: ClickToCallButtonProps) {
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [logDialogOpen, setLogDialogOpen] = useState(false);
  const [callLogId, setCallLogId] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const formatPhoneNumber = (phone: string): string => {
    const cleaned = phone.replace(/[^\d+]/g, "");
    if (cleaned.startsWith("+358")) {
      const local = cleaned.slice(4);
      return `+358 ${local.slice(0, 2)} ${local.slice(2, 5)} ${local.slice(5)}`;
    }
    if (cleaned.startsWith("0")) {
      return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6)}`;
    }
    return phone;
  };

  const getTelLink = (phone: string): string => {
    const cleaned = phone.replace(/\s/g, "");
    if (cleaned.startsWith("0")) {
      return `tel:+358${cleaned.slice(1)}`;
    }
    return `tel:${cleaned}`;
  };

  const logCallMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase
        .from("communication_logs")
        .insert({
          order_id: orderId || null,
          customer_id: customerId || null,
          communication_type: "phone",
          direction: "outbound",
          status: "initiated",
          to_address: phoneNumber,
          summary: `Puhelu aloitettu: ${customerName}`,
          metadata: {
            customer_name: customerName,
            initiated_at: new Date().toISOString(),
          },
        })
        .select("id")
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      setCallLogId(data.id);
      queryClient.invalidateQueries({ queryKey: ["communication-logs"] });
    },
  });

  const handleCallClick = () => {
    if (!phoneNumber) {
      toast.error("Puhelinnumeroa ei ole määritetty");
      return;
    }
    setConfirmDialogOpen(true);
  };

  const handleConfirmCall = async () => {
    if (!phoneNumber) return;
    try {
      await logCallMutation.mutateAsync();
    } catch {}
    window.location.href = getTelLink(phoneNumber);
    setConfirmDialogOpen(false);
    toast.success("Puhelu käynnistetty", {
      description: "Haluatko lisätä muistiinpanot puhelun jälkeen?",
      action: {
        label: "Lisää muistiinpanot",
        onClick: () => setLogDialogOpen(true),
      },
      duration: 10000,
    });
  };

  if (!phoneNumber) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground cursor-not-allowed opacity-50" disabled>
            <Phone className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent><p>Puhelinnumeroa ei ole määritetty</p></TooltipContent>
      </Tooltip>
    );
  }

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-green-600 hover:text-green-700 hover:bg-green-50"
            onClick={handleCallClick}
          >
            <Phone className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent><p>Soita: {formatPhoneNumber(phoneNumber)}</p></TooltipContent>
      </Tooltip>

      <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PhoneCall className="h-5 w-5 text-green-600" />
              Soita asiakkaalle
            </DialogTitle>
            <DialogDescription>Puhelu kirjataan automaattisesti viestintähistoriaan.</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="bg-muted rounded-lg p-4 space-y-2">
              <p className="font-medium">{customerName}</p>
              <p className="text-lg font-mono">{formatPhoneNumber(phoneNumber)}</p>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setConfirmDialogOpen(false)}>Peruuta</Button>
            <Button onClick={handleConfirmCall} className="gap-2 bg-green-600 hover:bg-green-700" disabled={logCallMutation.isPending}>
              <Phone className="h-4 w-4" />
              Soita
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <LogPhoneCallDialog
        open={logDialogOpen}
        onOpenChange={setLogDialogOpen}
        orderId={orderId}
        customerId={customerId}
        existingLogId={callLogId}
        defaultValues={{
          direction: "outbound",
          phoneNumber: phoneNumber,
          contactName: customerName,
        }}
      />
    </>
  );
}
